import React, { useState, useEffect, useRef } from 'react';
import styles from './Media.module.css';
import Overlay from './Overlay';
import Lightbox from './Lightbox';
import { StreamThumb, VideoOverlay } from './StreamVideo';

// Signed media urls (R2 1 h, Stream 4 h) go stale in an idle tab: refetch the
// library when it has been this long since the last load.
const MEDIA_STALE_MS = 45 * 60 * 1000;
import * as tus from 'tus-js-client';

function fmtDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d : d.toISOString();
  return new Date(s.slice(0, 10) + 'T12:00:00')
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// PUT a file to a presigned R2 url, reporting 0..100 progress. fetch() has no
// upload-progress events, so this is XHR on purpose.
function putWithProgress(url, file, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = e => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300)
      ? resolve()
      : reject(new Error(`Upload failed for ${file.name} (${xhr.status})`));
    xhr.onerror = () => reject(new Error(`Upload failed for ${file.name}`));
    xhr.send(file);
  });
}

// Resumable tus upload straight to Cloudflare Stream. `uploadUrl` is a
// direct-creator-upload url minted by our backend, so tus skips its own create
// step and just streams chunks. Stream requires chunks to be a multiple of
// 256 KiB and at least 5 MiB; 50 MiB keeps request count low on big clips.
function uploadToStream(file, uploadUrl, onProgress) {
  return new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      uploadUrl,
      chunkSize: 50 * 1024 * 1024,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      storeFingerprintForResuming: false,
      onProgress: (sent, total) => onProgress(total ? Math.round((sent / total) * 100) : 0),
      onError: err => {
        // tus messages embed the internal upload url; keep that in the console only.
        console.error('[tus]', err);
        const status = err && err.originalResponse && typeof err.originalResponse.getStatus === 'function'
          ? err.originalResponse.getStatus() : null;
        reject(new Error(`Upload failed for ${file.name}${status ? ` (${status})` : ''}`));
      },
      onSuccess: () => resolve(),
    });
    upload.start();
  });
}

// ─── One post in the library grid ───────────────────────────────────────────
function PostCard({ post, onDelete, ensureFresh }) {
  const [lightbox, setLightbox] = useState(null); // { index } | null
  const [player, setPlayer] = useState(null);     // streamUid being played | null
  // Derive the file from the CURRENT post so a refetch (fresh signed urls)
  // flows straight into the open player instead of a captured stale copy.
  const playerFile = player ? (post.files || []).find(x => x.streamUid === player) : null;
  const recipients = post.visibility === 'all_clients'
    ? 'All Families'
    : (post.athletes || []).map(a => a.name).join(', ') || 'No recipients';
  // Photos drive the lightbox; videos keep their inline controls.
  const photos = (post.files || []).filter(f => f.type === 'photo');
  return (
    <div className={styles.card}>
      <div className={styles.cardMedia}>
        {(post.files || []).map((f, i) => (
          f.streamUid
            ? <StreamThumb
                key={i}
                file={f}
                className={styles.thumb}
                style={{ aspectRatio: '16 / 9' }}
                onClick={async () => { await ensureFresh(); setPlayer(f.streamUid); }}
              />
            : f.type === 'video'
            ? <video key={i} className={styles.thumb} src={f.url} controls preload="metadata" />
            : <img
                key={i}
                className={`${styles.thumb} ${styles.thumbClickable}`}
                src={f.url}
                alt={f.filename}
                onClick={() => setLightbox({ index: photos.indexOf(f) })}
              />
        ))}
      </div>
      {playerFile && (
        <VideoOverlay file={playerFile} caption={post.caption} onClose={() => setPlayer(null)} />
      )}
      {lightbox && (
        <Lightbox
          photos={photos}
          index={lightbox.index}
          caption={post.caption}
          onClose={() => setLightbox(null)}
        />
      )}
      {post.caption && <div className={styles.cardCaption}>{post.caption}</div>}
      <div className={styles.cardMeta}>
        <span className={`${styles.pill} ${post.visibility === 'all_clients' ? styles.pillAll : styles.pillAthletes}`}>
          {recipients}
        </span>
      </div>
      <div className={styles.cardFooter}>
        <span className={styles.cardDate}>
          {(post.files || []).length} file{(post.files || []).length === 1 ? '' : 's'} · {fmtDate(post.createdAt)}
        </span>
        <button className={styles.deleteBtn} onClick={onDelete}>Delete</button>
      </div>
    </div>
  );
}

// ─── Upload modal ───────────────────────────────────────────────────────────
function UploadModal({ clients, API, authHeader, showToast, onClose, onDone }) {
  const [files, setFiles] = useState([]);            // File[]
  const [caption, setCaption] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [selected, setSelected] = useState([]);       // [{ id, name, family }]
  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState('');               // '' | progress text
  const [progress, setProgress] = useState(null);     // 0..100 while a file uploads, else null
  const fileInputRef = useRef(null);
  // File -> completed upload target. Survives a failed attempt inside this
  // modal, so retrying after "file 4 of 5 failed" doesn't re-upload 1–3.
  const doneRef = useRef(new Map());

  // Last name = last token of the family (client) name, matching familyName() in
  // Clients.js. The picker is ordered by family last name (then full family, then
  // athlete name) so the coach can scan to a family quickly.
  const lastName = (s) => (s || '').trim().split(/\s+/).pop().toLowerCase();
  const byFamilyLastName = (a, b) =>
    lastName(a.family).localeCompare(lastName(b.family)) ||
    (a.family || '').localeCompare(b.family || '') ||
    (a.name || '').localeCompare(b.name || '');
  const allAthletes = (clients || []).flatMap(c =>
    (c.athletes || []).map(a => ({ id: a.id, name: a.name, family: c.name }))
  ).sort(byFamilyLastName);
  const selectedIds = new Set(selected.map(a => a.id));
  // Show every unselected athlete by default; narrow as the dev types.
  const q = search.trim().toLowerCase();
  const matches = allAthletes
    .filter(a => !selectedIds.has(a.id))
    .filter(a => !q || (`${a.name} ${a.family}`).toLowerCase().includes(q));

  const addFiles = (list) => {
    const picked = Array.from(list).filter(f =>
      f.type.startsWith('image/') || f.type.startsWith('video/'));
    if (picked.length !== list.length) {
      showToast('Only image and video files are allowed', 'error');
    }
    setFiles(prev => [...prev, ...picked]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const toggleAthlete = (a) => setSelected(prev => [...prev, a]);
  const removeAthlete = (id) => setSelected(prev => prev.filter(a => a.id !== id));

  const handleSubmit = async () => {
    if (files.length === 0) { showToast('Add at least one photo or video', 'error'); return; }
    if (!sendToAll && selected.length === 0) {
      showToast('Pick athletes, or turn on Send to All Families', 'error');
      return;
    }
    try {
      // 1 — mint one upload target per file that hasn't already been uploaded
      //     (videos → Stream, photos → R2). size is required: Stream's tus
      //     upload needs the byte length up front.
      const done = doneRef.current;
      const pendingFiles = files.filter(f => !done.has(f));
      let minted = [];
      if (pendingFiles.length > 0) {
        setBusy('Preparing upload…');
        const urlRes = await fetch(`${API}/media/upload-urls`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({
            files: pendingFiles.map(f => ({ filename: f.name, contentType: f.type, size: f.size })),
          }),
        });
        if (!urlRes.ok) {
          const e = await urlRes.json().catch(() => ({}));
          showToast(e.error || 'Could not start upload', 'error');
          setBusy('');
          return;
        }
        const { uploads } = await urlRes.json();
        // Targets are positional: anything but a 1:1 list would pair the wrong
        // file with the wrong destination, so refuse rather than guess.
        if (!Array.isArray(uploads) || uploads.length !== pendingFiles.length) {
          throw new Error('Upload setup failed — please try again');
        }
        minted = uploads;
      }

      // 2 — upload each pending file straight to storage. Bytes never pass
      //     through our server either way; the modal shows per-file progress.
      for (let i = 0; i < pendingFiles.length; i++) {
        const file = pendingFiles[i];
        const target = minted[i];
        const label = `Uploading ${files.indexOf(file) + 1} of ${files.length}`;
        setBusy(`${label}…`);
        setProgress(0);
        const onProgress = pct => { setProgress(pct); setBusy(`${label} · ${pct}%`); };
        if (target.kind === 'stream') {
          await uploadToStream(file, target.uploadUrl, onProgress);
        } else {
          await putWithProgress(target.uploadUrl, file, target.contentType, onProgress);
        }
        done.set(file, target);
      }
      setProgress(null);

      // 3 — create the post
      setBusy('Finishing…');
      const postRes = await fetch(`${API}/media/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({
          caption,
          visibility: sendToAll ? 'all_clients' : 'athletes',
          athleteIds: selected.map(a => a.id),
          files: files.map(f => {
            const t = done.get(f);
            return {
              ...(t.kind === 'stream' ? { streamUid: t.uid } : { key: t.key }),
              filename: f.name,
              contentType: t.contentType,
              size: f.size,
            };
          }),
        }),
      });
      if (!postRes.ok) {
        const e = await postRes.json().catch(() => ({}));
        showToast(e.error || 'Could not save post', 'error');
        setBusy('');
        return;
      }
      showToast('Media posted');
      onDone();
    } catch (err) {
      showToast(err.message || 'Upload failed', 'error');
      setBusy('');
      setProgress(null);
    }
  };

  return (
    <Overlay className={styles.overlay} onClose={busy ? undefined : onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle}>Upload Media</h3>
          {!busy && <button className={styles.modalClose} onClick={onClose}>✕</button>}
        </div>
        <div className={styles.modalBody}>
          {/* Drop zone */}
          <div
            className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className={styles.dropIcon}>▲</div>
            <div>Drag photos or videos here, or <span className={styles.dropLink}>browse</span></div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*"
              style={{ display: 'none' }}
              onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </div>

          {/* Selected files */}
          {files.length > 0 && (
            <div className={styles.fileList}>
              {files.map((f, i) => (
                <div key={i} className={styles.fileRow}>
                  <span className={styles.fileName}>{f.name}</span>
                  <span className={styles.fileSize}>{fmtSize(f.size)}</span>
                  <button
                    className={styles.fileRemove}
                    onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}
                  >✕</button>
                </div>
              ))}
            </div>
          )}

          {/* Upload progress (one bar, current file) */}
          {progress !== null && (
            <div className={styles.progressWrap} role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
              <div className={styles.progressBar} style={{ width: `${progress}%` }} />
            </div>
          )}

          {/* Caption */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Caption (optional)</label>
            <textarea
              className={styles.textarea}
              rows={2}
              value={caption}
              onChange={e => setCaption(e.target.value)}
              placeholder="e.g. Great footwork at Saturday's session!"
            />
          </div>

          {/* Recipients */}
          <div className={styles.field}>
            <label className={styles.fieldLabel}>Send to</label>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={sendToAll}
                onChange={e => setSendToAll(e.target.checked)}
              />
              <span><strong>All Families</strong> — every parent sees this post</span>
            </label>

            {!sendToAll && (
              <>
                {selected.length > 0 && (
                  <div className={styles.chips}>
                    {selected.map(a => (
                      <span key={a.id} className={styles.chip}>
                        {a.name}
                        <button onClick={() => removeAthlete(a.id)}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
                <input
                  className={styles.searchInput}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search athletes by name…"
                />
                <div className={styles.athleteList}>
                  {matches.length === 0 ? (
                    <div className={styles.athleteEmpty}>
                      {allAthletes.length === selected.length
                        ? 'All athletes already selected'
                        : `No athletes match "${search}"`}
                    </div>
                  ) : (
                    matches.map(a => (
                      <button
                        key={a.id}
                        className={styles.athleteOption}
                        onClick={() => { toggleAthlete(a); setSearch(''); }}
                      >
                        <span>{a.name}</span>
                        <span className={styles.athleteFamily}>{a.family}</span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>

          <div className={styles.modalActions}>
            <button className={styles.cancelBtn} disabled={!!busy} onClick={onClose}>Cancel</button>
            <button className={styles.submitBtn} disabled={!!busy} onClick={handleSubmit}>
              {busy || 'Post Media'}
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function Media({ clients, showToast, API, authHeader }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [librarySearch, setLibrarySearch] = useState('');

  // Real-time library filter: matches across caption, athlete names, family names.
  const lq = librarySearch.trim().toLowerCase();
  const filteredPosts = !lq ? posts : posts.filter(p => {
    if ((p.caption || '').toLowerCase().includes(lq)) return true;
    return (p.athletes || []).some(a =>
      (a.name   || '').toLowerCase().includes(lq) ||
      (a.family || '').toLowerCase().includes(lq)
    );
  });

  const postsLoadedAt = useRef(0);
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/media/posts`, { headers: authHeader() });
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
      postsLoadedAt.current = Date.now();
    } catch {
      showToast('Could not load media', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchPosts(); }, []);

  // Signed urls expire: refetch when the tab wakes after sitting idle, and
  // right before a player opens if the list is stale (PostCard calls ensureFresh).
  const isStale = () => Date.now() - postsLoadedAt.current > MEDIA_STALE_MS;
  const ensureFresh = async () => { if (isStale()) await fetchPosts(); };
  useEffect(() => {
    const onWake = () => { if (document.visibilityState !== 'hidden' && isStale()) fetchPosts(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post? This removes the media from every parent portal.')) return;
    try {
      const res = await fetch(`${API}/media/posts/${id}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) { showToast('Delete failed', 'error'); return; }
      showToast('Post deleted');
      fetchPosts();
    } catch {
      showToast('Network error', 'error');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <h1 className={styles.pageTitle}>Media</h1>
          <p className={styles.pageSubtitle}>Share photos and videos to your families' portals.</p>
        </div>
        <button className={styles.addBtn} onClick={() => setShowUpload(true)}>
          ＋ Upload Media
        </button>
      </div>

      {!loading && posts.length > 0 && (
        <input
          className={styles.libSearch}
          value={librarySearch}
          onChange={e => setLibrarySearch(e.target.value)}
          placeholder="Search media by caption, athlete, or family…"
        />
      )}

      {loading ? (
        <p className={styles.muted}>Loading…</p>
      ) : posts.length === 0 ? (
        <p className={styles.muted}>No media posted yet. Click Upload Media to share photos or videos.</p>
      ) : filteredPosts.length === 0 ? (
        <p className={styles.muted}>No media matches "{librarySearch}".</p>
      ) : (
        <div className={styles.grid}>
          {filteredPosts.map(p => (
            <PostCard key={p.id} post={p} onDelete={() => handleDelete(p.id)} ensureFresh={ensureFresh} />
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          clients={clients}
          API={API}
          authHeader={authHeader}
          showToast={showToast}
          onClose={() => setShowUpload(false)}
          onDone={() => { setShowUpload(false); fetchPosts(); }}
        />
      )}
    </div>
  );
}
